import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, ChevronsUpDown, Download, Loader2, Plus, Printer, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { sanitizeUserDisplay } from '@/lib/user-display';
import { cn } from '@/lib/utils';
import { downloadCsv, escapeHtml, openPrintableHtml } from '@/lib/wms-document-output';
import { usePageParams } from '@/hooks/usePageParams';
import { useRenderMode } from '@/hooks/useRenderMode';
import WmsMaterialRequestMobileFlow from './WmsMaterialRequestMobileFlow';

type RequestForm = 'PIPE' | 'PLATE' | 'GENERAL';

type SkuOption = {
  id: string;
  sku_code: string;
  name: string;
  category?: string | null;
  uom?: string | null;
  material_grade?: string | null;
  pipe_type?: string | null;
  nominal_size?: string | null;
  schedule?: string | null;
  pipe_length?: string | null;
  thickness_mm?: number | null;
  width_mm?: number | null;
  length_mm?: number | null;
};

type RequestLineMeta = {
  materialType?: string;
  smls?: boolean;
  wld?: boolean;
  nominalSize?: string;
  schedule?: string;
  length?: string;
  pipeCode?: string;
  stockNo?: string;
  heatNo?: string;
  millCert?: boolean;
  dimension?: string;
  grade?: string;
  location?: string;
  checkedRef?: string;
  receivedByContractorDate?: string;
  returnQty?: string;
  irtNo?: string;
  lineDate?: string;
  qtyIssued?: string;
  pieceQty?: string;
  requestLength?: string;
  remark?: string;
};

type RequestLine = {
  id: string;
  line_number: number;
  sku_code: string | null;
  sku_name: string | null;
  qty_ordered: number;
  qty_fulfilled: number;
  uom: string | null;
  lot_number: string | null;
  batch_code: string | null;
  notes: string | null;
  metadata?: Record<string, unknown> | null;
};

type RequestDetail = {
  id: string;
  order_number: string;
  order_type: string;
  project_id: string | null;
  project_code: string | null;
  counterparty_name: string | null;
  department: string | null;
  requester_name: string | null;
  required_date: string | null;
  priority: string | null;
  status: string;
  notes: string | null;
  metadata?: Record<string, unknown> | null;
  total_qty: number;
  total_fulfilled: number;
  lines: RequestLine[];
};

type ProjectOption = { id: string; project_code: string; project_name?: string | null };
type UserOption = { id: string; display_name: string; email?: string | null };

type HeaderDraft = {
  projectId: string;
  contractor: string;
  jobNo: string;
  projectCode: string;
  requestedBy: string;
  authorisedBy: string;
  approvedBy: string;
  issuedBy: string;
  updatedBy: string;
  requestDate: string;
  priority: string;
  notes: string;
  requestForm: string;
  approvalRequired: string;
};

const HEADER_REQUEST_FORM_OPTIONS = [
  { value: 'PIPE', label: 'Pipe Request Slip' },
  { value: 'PLATE', label: 'Plate Request Form' },
  { value: 'GENERAL', label: 'General Material Request' },
];

const LINE_MATERIAL_TYPE_OPTIONS = [
  { value: 'PIPE', label: 'PIPE' },
  { value: 'PLATE', label: 'PLATE' },
  { value: 'OTHER', label: 'OTHER' },
];

const LINE_UOM_OPTIONS = ['MM', 'EA', 'PCS', 'BOX', 'CARTON', 'PALLET', 'KG', 'M', 'CM', 'CBM'];

type LineDraft = {
  id: string;
  isNew: boolean;
  skuCode: string;
  skuName: string;
  qtyReq: string;
  uom: string;
  materialType: string;
  smls: boolean;
  wld: boolean;
  nominalSize: string;
  schedule: string;
  length: string;
  pipeCode: string;
  stockNo: string;
  heatNo: string;
  millCert: boolean;
  dimension: string;
  grade: string;
  location: string;
  checkedRef: string;
  receivedByContractorDate: string;
  returnQty: string;
  irtNo: string;
  lineDate: string;
  qtyIssued: string;
  remark: string;
};

function buildPlateDimension(sku: SkuOption) {
  const parts = [sku.thickness_mm, sku.width_mm, sku.length_mm]
    .filter((value) => value !== null && value !== undefined)
    .map((value) => String(value));
  return parts.length ? parts.join(' x ') : '';
}

function buildSkuPatch(sku: SkuOption, requestForm: RequestForm, currentLine: LineDraft): Partial<LineDraft> {
  const pipeType = String(sku.pipe_type ?? '').toUpperCase();
  const isSmls = pipeType.includes('SMLS');
  const isWld = pipeType.includes('WLD') || pipeType.includes('WELD');

  if (requestForm === 'PIPE') {
    return {
      skuCode: sku.sku_code,
      skuName: sku.name,
      uom: 'MM',
      smls: isSmls,
      wld: isWld,
      nominalSize: sku.nominal_size ?? currentLine.nominalSize,
      schedule: sku.schedule ?? currentLine.schedule,
      length: sku.pipe_length ?? (sku.length_mm ? String(sku.length_mm) : currentLine.length),
      grade: sku.material_grade ?? currentLine.grade,
    };
  }

  if (requestForm === 'PLATE') {
    return {
      skuCode: sku.sku_code,
      skuName: sku.name,
      uom: sku.uom ?? currentLine.uom ?? 'MM',
      dimension: buildPlateDimension(sku) || currentLine.dimension,
      grade: sku.material_grade ?? currentLine.grade,
    };
  }

  return {
    skuCode: sku.sku_code,
    skuName: sku.name,
    uom: sku.uom ?? currentLine.uom ?? 'MM',
  };
}

function MaterialRequestSkuPicker({
  warehouseId,
  projectId,
  requestForm,
  value,
  disabled,
  onSelect,
}: {
  warehouseId?: string;
  projectId?: string;
  requestForm: RequestForm;
  value: string;
  disabled?: boolean;
  onSelect: (sku: SkuOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<SkuOption[]>([]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    if (!projectId) {
      setOptions([]);
      setLoading(false);
      return () => { cancelled = true; };
    }
    const params = new URLSearchParams({ limit: '80', projectId });
    if (warehouseId) params.set('warehouseId', warehouseId);
    if (requestForm !== 'GENERAL') params.set('category', requestForm);
    if (query.trim()) params.set('search', query.trim());
    void api.get<any>(`/wms/inventory?${params.toString()}`)
      .then((response) => {
        const rows = Array.isArray(response) ? response : (response?.items ?? response?.data ?? []);
        const deduped = new Map<string, SkuOption>();
        for (const row of rows) {
          const skuCode = String(row?.sku_code ?? '').trim();
          if (!skuCode || deduped.has(skuCode)) continue;
          deduped.set(skuCode, {
            id: String(row?.sku_id ?? row?.id ?? skuCode),
            sku_code: skuCode,
            name: String(row?.sku_name ?? row?.name ?? skuCode),
            category: row?.category ?? null,
            uom: row?.uom ?? null,
            material_grade: row?.material_grade ?? null,
            pipe_type: row?.pipe_type ?? null,
            nominal_size: row?.nominal_size ?? null,
            schedule: row?.schedule ?? null,
            pipe_length: row?.pipe_length ?? null,
            thickness_mm: row?.thickness_mm ?? null,
            width_mm: row?.width_mm ?? null,
            length_mm: row?.length_mm ?? null,
          });
        }
        if (!cancelled) setOptions(Array.from(deduped.values()));
      })
      .catch(() => {
        if (!cancelled) setOptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, query, requestForm, warehouseId, projectId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="w-full justify-between" disabled={disabled}>
          <span className="truncate text-left">{value || 'Search SKU by code or name'}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder="Search SKU code or name..." />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading SKU list...
              </div>
            ) : null}
            {!loading ? <CommandEmpty>{projectId ? 'No matching project inventory found.' : 'Select project first.'}</CommandEmpty> : null}
            <CommandGroup>
              {options.map((sku) => (
                <CommandItem
                  key={sku.id}
                  value={`${sku.sku_code} ${sku.name}`}
                  onSelect={() => {
                    onSelect(sku);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <Check className={cn('mr-2 h-4 w-4', value.startsWith(sku.sku_code) ? 'opacity-100' : 'opacity-0')} />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{sku.sku_code}</div>
                    <div className="truncate text-xs text-muted-foreground">{sku.name}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function requestByLabel(requestForm: string) {
  if (requestForm === 'PIPE') return 'Request By';
  if (requestForm === 'PLATE') return 'Initiated By';
  return 'Requested By';
}

function issuedByLabel(requestForm: string) {
  return requestForm === 'PIPE' ? 'Issue By' : 'Issued By';
}

function formatDate(value: string | null | undefined) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toISOString().slice(0, 10);
}

function parseBooleanFlag(value: unknown) {
  if (value === true) return true;
  const normalized = String(value ?? '').trim().toUpperCase();
  return normalized === 'YES' || normalized === 'Y' || normalized === 'TRUE' || normalized === '1';
}

function normalizeYesNo(value: unknown, fallback = 'NO') {
  const normalized = String(value ?? '').trim().toUpperCase();
  if (normalized === 'YES' || normalized === 'Y' || normalized === 'TRUE' || normalized === '1') return 'YES';
  if (normalized === 'NO' || normalized === 'N' || normalized === 'FALSE' || normalized === '0') return 'NO';
  return fallback;
}

function boolLabel(value: boolean) {
  return value ? 'YES' : 'NO';
}

function asMeta(value: Record<string, unknown> | null | undefined): RequestLineMeta {
  return (value ?? {}) as RequestLineMeta;
}

function inferMaterialType(line: RequestLine): string {
  const meta = asMeta(line.metadata);
  const explicit = String(meta.materialType ?? '').toUpperCase();
  if (explicit === 'PIPE' || explicit === 'PLATE' || explicit === 'OTHER') return explicit;

  const haystack = `${line.sku_code ?? ''} ${line.sku_name ?? ''} ${line.batch_code ?? ''}`.toUpperCase();
  if (haystack.includes('PLATE')) return 'PLATE';
  if (haystack.includes('PIPE')) return 'PIPE';
  return 'OTHER';
}

function inferRequestForm(order: RequestDetail): string {
  const explicit = String(order.metadata?.requestForm ?? '').toUpperCase();
  if (explicit === 'PIPE' || explicit === 'PLATE' || explicit === 'GENERAL') return explicit;

  const detectedTypes = new Set((order.lines ?? []).map(inferMaterialType));
  if (detectedTypes.has('PIPE') && !detectedTypes.has('PLATE')) return 'PIPE';
  if (detectedTypes.has('PLATE') && !detectedTypes.has('PIPE')) return 'PLATE';
  return 'GENERAL';
}

function buildHeaderDraft(order: RequestDetail): HeaderDraft {
  const resolvedProjectCode = order.project_code ?? order.department ?? '';
  return {
    projectId: order.project_id ?? '',
    contractor: order.counterparty_name ?? '',
    jobNo: resolvedProjectCode,
    projectCode: resolvedProjectCode,
    requestedBy: sanitizeUserDisplay(order.requester_name ?? ''),
    authorisedBy: sanitizeUserDisplay(String(order.metadata?.authorisedBy ?? order.metadata?.approvedBy ?? '')),
    approvedBy: sanitizeUserDisplay(String(order.metadata?.approvedBy ?? '')),
    issuedBy: sanitizeUserDisplay(String(order.metadata?.issuedBy ?? '')),
    updatedBy: String(order.metadata?.updatedBy ?? ''),
    requestDate: formatDate(order.required_date),
    priority: order.priority ?? 'NORMAL',
    notes: order.notes ?? '',
    requestForm: inferRequestForm(order),
    approvalRequired: String(order.metadata?.approvalRequired ?? order.metadata?.requestApprovalRequired ?? true) === 'false' ? 'false' : 'true',
  };
}

function newHeaderDraft(requestForm: RequestForm): HeaderDraft {
  return {
    projectId: '',
    contractor: '',
    jobNo: '',
    projectCode: '',
    requestedBy: '',
    authorisedBy: '',
    approvedBy: '',
    issuedBy: '',
    updatedBy: '',
    requestDate: formatDate(new Date().toISOString()),
    priority: 'NORMAL',
    notes: '',
    requestForm,
    approvalRequired: 'true',
  };
}

function buildLineDraft(line: RequestLine): LineDraft {
  const meta = asMeta(line.metadata);
  return {
    id: line.id,
    isNew: false,
    skuCode: line.sku_code ?? '',
    skuName: line.sku_name ?? '',
    qtyReq: String(meta.pieceQty ?? line.qty_ordered ?? 0),
    uom: line.uom ?? 'MM',
    materialType: inferMaterialType(line),
    smls: Boolean(meta.smls),
    wld: Boolean(meta.wld),
    nominalSize: String(meta.nominalSize ?? ''),
    schedule: String(meta.schedule ?? ''),
    length: String(meta.requestLength ?? meta.length ?? ''),
    pipeCode: String(meta.pipeCode ?? line.batch_code ?? ''),
    stockNo: String(meta.stockNo ?? line.batch_code ?? ''),
    heatNo: String(meta.heatNo ?? line.lot_number ?? ''),
    millCert: parseBooleanFlag(meta.millCert),
    dimension: String(meta.dimension ?? ''),
    grade: String(meta.grade ?? ''),
    location: String(meta.location ?? ''),
    checkedRef: normalizeYesNo(meta.checkedRef),
    receivedByContractorDate: String(meta.receivedByContractorDate ?? ''),
    returnQty: String(meta.returnQty ?? ''),
    irtNo: String(meta.irtNo ?? ''),
    lineDate: formatDate(String(meta.lineDate ?? '')),
    qtyIssued: String(meta.qtyIssued ?? line.qty_fulfilled ?? 0),
    remark: String(meta.remark ?? line.notes ?? ''),
  };
}

function newLineDraft(): LineDraft {
  return {
    id: `new-${Math.random().toString(36).slice(2, 8)}`,
    isNew: true,
    skuCode: '',
    skuName: '',
    qtyReq: '1',
    uom: 'MM',
    materialType: 'PIPE',
    smls: false,
    wld: false,
    nominalSize: '',
    schedule: '',
    length: '0',
    pipeCode: '',
    stockNo: '',
    heatNo: '',
    millCert: false,
    dimension: '',
    grade: '',
    location: '',
    checkedRef: 'NO',
    receivedByContractorDate: '',
    returnQty: '0',
    irtNo: '',
    lineDate: '',
    qtyIssued: '0',
    remark: '',
  };
}

function statusTone(status: string | null | undefined) {
  switch (status) {
    case 'FULFILLED':
    case 'CLOSED':
      return 'bg-green-100 text-green-700';
    case 'PROCESSING':
    case 'PARTIALLY_FULFILLED':
      return 'bg-blue-100 text-blue-700';
    case 'CANCELLED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

type WmsMaterialRequestDetailPanelProps = {
  id?: string;
  requestFormPreset?: RequestForm;
  embedded?: boolean;
  onClose?: () => void;
};

function DesktopMaterialRequestDetailPage({
  id: idProp,
  requestFormPreset = 'PIPE',
  embedded = false,
  onClose,
}: WmsMaterialRequestDetailPanelProps) {
  const navigate = useNavigate();
  const params = usePageParams<{ id: string }>();
  const [activeOrderId, setActiveOrderId] = useState(idProp ?? params.id ?? '');
  const [order, setOrder] = useState<RequestDetail | null>(null);
  const [headerDraft, setHeaderDraft] = useState<HeaderDraft | null>(() => newHeaderDraft(requestFormPreset));
  const [lineDrafts, setLineDrafts] = useState<LineDraft[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingMaster, setLoadingMaster] = useState(true);
  const [loading, setLoading] = useState(Boolean(idProp ?? params.id));
  const [savingHeader, setSavingHeader] = useState(false);
  const [savingLineId, setSavingLineId] = useState<string | null>(null);
  const [savingLines, setSavingLines] = useState(false);
  const [approving, setApproving] = useState(false);
  const warehouseId = useMemo(() => String(order?.metadata?.warehouseId ?? order?.metadata?.warehouse_id ?? ''), [order?.metadata]);

  const fetchOrder = useCallback(async () => {
    if (!activeOrderId) {
      setOrder(null);
      setHeaderDraft((current) => current ?? newHeaderDraft(requestFormPreset));
      setLineDrafts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const detail = await api.get<RequestDetail>(`/wms/business-orders/${activeOrderId}`);
      setOrder(detail);
      setHeaderDraft(buildHeaderDraft(detail));
      const loadedLines = (detail.lines ?? []).map(buildLineDraft);
      setLineDrafts(loadedLines);
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to load material request');
    } finally {
      setLoading(false);
    }
  }, [activeOrderId, requestFormPreset]);

  useEffect(() => {
    setActiveOrderId(idProp ?? params.id ?? '');
  }, [idProp, params.id]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    let cancelled = false;
    setLoadingMaster(true);
    void Promise.all([
      api.get<ProjectOption[]>('/wms/projects'),
      api.get<UserOption[]>('/wms/users'),
    ])
      .then(([projectRows, userRows]) => {
        if (cancelled) return;
        setProjects(projectRows);
        setUsers(userRows);
      })
      .catch((err: any) => {
        if (!cancelled) toast.error(err?.message ?? 'Failed to load projects / users');
      })
      .finally(() => {
        if (!cancelled) setLoadingMaster(false);
      });
    return () => { cancelled = true; };
  }, []);

  const closeOrBack = () => {
    if (embedded) {
      onClose?.();
      return;
    }
    navigate('/wms/material-requests');
  };

  const requestTitle = useMemo(() => {
    if (!headerDraft?.requestForm) return 'Material Request';
    if (headerDraft.requestForm === 'PLATE') return 'Plate Request Form';
    if (headerDraft.requestForm === 'GENERAL') return 'General Material Request';
    return 'Pipe Request Slip';
  }, [headerDraft?.requestForm]);

  const patchLine = (lineId: string, patch: Partial<LineDraft>) => {
    setLineDrafts((prev) => prev.map((line) => (line.id === lineId ? { ...line, ...patch } : line)));
  };

  const canSubmit = String(order?.status ?? '') === 'DRAFT';
  const canApprove = String(order?.status ?? '') === 'SUBMITTED';
  const canReject = String(order?.status ?? '').toUpperCase() === 'SUBMITTED';
  const canRevise = String(order?.status ?? '').toUpperCase() === 'SUBMITTED';
  const isEditableDraft = !order || String(order.status ?? '').toUpperCase() === 'DRAFT';
  const approvalOwnerLabel = headerDraft
    ? (headerDraft.requestForm === 'PIPE' ? (headerDraft.authorisedBy || '-') : (headerDraft.approvedBy || '-'))
    : '-';

  const saveHeader = async (options?: { silent?: boolean }): Promise<string | null> => {
    if (!headerDraft) return null;
    if (!headerDraft.projectId) {
      toast.error('Project is required');
      return null;
    }
    if (!headerDraft.requestedBy) {
      toast.error('Request by is required');
      return null;
    }
    if (headerDraft.requestForm === 'PIPE' && !headerDraft.authorisedBy) {
      toast.error('Authorised by is required');
      return null;
    }
    if (headerDraft.requestForm !== 'PIPE' && !headerDraft.approvedBy) {
      toast.error('Approved by is required');
      return null;
    }
    setSavingHeader(true);
    try {
      const selectedProject = projects.find((project) => project.id === headerDraft.projectId) ?? null;
      if (!activeOrderId) {
        const created = await api.post<RequestDetail>('/wms/business-orders', {
          projectId: headerDraft.projectId,
          orderType: 'MATERIAL_REQUEST',
          direction: 'INTERNAL',
          counterpartyName: headerDraft.contractor || undefined,
          department: headerDraft.projectCode || undefined,
          requesterName: headerDraft.requestedBy || undefined,
          orderDate: headerDraft.requestDate || undefined,
          requiredDate: headerDraft.requestDate || undefined,
          priority: headerDraft.priority || 'NORMAL',
          notes: headerDraft.notes || undefined,
          metadata: {
            requestForm: headerDraft.requestForm,
            approvalRequired: headerDraft.approvalRequired !== 'false',
            requestApprovalRequired: headerDraft.approvalRequired !== 'false',
            authorisedBy: headerDraft.authorisedBy || '',
            approvedBy: headerDraft.requestForm === 'PIPE' ? (headerDraft.authorisedBy || '') : (headerDraft.approvedBy || ''),
            issuedBy: headerDraft.issuedBy || '',
            updatedBy: headerDraft.updatedBy || '',
          },
        });
        setActiveOrderId(created.id);
        setOrder(created);
        setHeaderDraft((current) => current ? {
          ...current,
          projectCode: selectedProject?.project_code ?? current.projectCode,
          jobNo: selectedProject?.project_code ?? current.jobNo,
        } : current);
        if (!options?.silent) toast.success('Material request created');
        await fetchOrder();
        return created.id;
      }

      await api.patch(`/wms/business-orders/${activeOrderId}`, {
        projectId: headerDraft.projectId,
        counterpartyName: headerDraft.contractor || undefined,
        department: headerDraft.projectCode || undefined,
        requesterName: headerDraft.requestedBy || undefined,
        orderDate: headerDraft.requestDate || undefined,
        requiredDate: headerDraft.requestDate || undefined,
        priority: headerDraft.priority || undefined,
        notes: headerDraft.notes || undefined,
        metadata: {
          ...(order?.metadata ?? {}),
          requestForm: headerDraft.requestForm,
          approvalRequired: headerDraft.approvalRequired !== 'false',
          requestApprovalRequired: headerDraft.approvalRequired !== 'false',
          authorisedBy: headerDraft.authorisedBy || '',
          approvedBy: headerDraft.requestForm === 'PIPE' ? (headerDraft.authorisedBy || '') : (headerDraft.approvedBy || ''),
          issuedBy: headerDraft.issuedBy || '',
          updatedBy: headerDraft.updatedBy || '',
        },
      });
      if (!options?.silent) toast.success('Request header updated');
      await fetchOrder();
      return activeOrderId;
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save request header');
      return null;
    } finally {
      setSavingHeader(false);
    }
  };

  const hasLineContent = (line: LineDraft) => (
    Boolean(line.skuCode || line.skuName || line.qtyReq || line.length || line.remark)
  );

  const buildLinePayload = (line: LineDraft) => {
    const requestForm = headerDraft!.requestForm;
    const heatNo = requestForm === 'PLATE' ? line.heatNo : '';
    const requestedLength = requestForm === 'PIPE' ? (line.length || line.qtyReq || '') : line.length;
    return {
      skuCode: line.skuCode || undefined,
      skuName: line.skuName || undefined,
      qtyOrdered: Number(line.qtyReq || 0),
      uom: line.uom || 'MM',
      lotNumber: heatNo || undefined,
      batchCode: requestForm === 'PLATE' ? (line.stockNo || undefined) : (line.pipeCode || undefined),
      notes: line.remark || undefined,
      metadata: {
        materialType: requestForm === 'GENERAL' ? (line.materialType || 'OTHER') : requestForm,
        smls: line.smls,
        wld: line.wld,
        nominalSize: line.nominalSize || '',
        schedule: line.schedule || '',
        length: line.length || '',
        requestLength: requestedLength,
        pieceQty: line.qtyReq || '1',
        pipeCode: line.pipeCode || '',
        stockNo: line.stockNo || '',
        heatNo,
        millCert: line.millCert,
        dimension: line.dimension || '',
        grade: line.grade || '',
        location: line.location || '',
        checkedRef: line.checkedRef || 'NO',
        receivedByContractorDate: line.receivedByContractorDate || '',
        returnQty: line.returnQty || '0',
        irtNo: line.irtNo || '',
        lineDate: line.lineDate || '',
        remark: line.remark || '',
      },
    };
  };

  const saveAllLines = async (options?: { silent?: boolean; orderIdOverride?: string }): Promise<boolean> => {
    const targetOrderId = options?.orderIdOverride ?? activeOrderId;
    if (!targetOrderId || !headerDraft) {
      toast.error('Create request header first');
      return false;
    }
    const linesToSave = lineDrafts.filter(hasLineContent);
    if (linesToSave.length === 0) {
      toast.error('No line to save');
      return false;
    }
    setSavingLines(true);
    try {
      const newLines = linesToSave.filter((line) => line.isNew);
      const existingLines = linesToSave.filter((line) => !line.isNew);
      if (newLines.length > 0) {
        setSavingLineId(newLines[0]?.id ?? null);
        await api.post(`/wms/business-orders/${targetOrderId}/lines`, { lines: newLines.map(buildLinePayload) });
      }
      for (const line of existingLines) {
        setSavingLineId(line.id);
        const payload = buildLinePayload(line);
        await api.patch(`/wms/business-order-lines/${line.id}`, payload);
      }
      if (!options?.silent) toast.success('Lines Saved');
      await fetchOrder();
      return true;
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to save request lines');
      return false;
    } finally {
      setSavingLineId(null);
      setSavingLines(false);
    }
  };

  const deleteLine = async (line: LineDraft) => {
    if (!activeOrderId) return;
    if (line.isNew) {
      setLineDrafts((prev) => prev.filter((item) => item.id !== line.id));
      return;
    }
    setSavingLineId(line.id);
    try {
      await api.delete(`/wms/business-order-lines/${line.id}`);
      toast.success('Request line removed');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to remove request line');
    } finally {
      setSavingLineId(null);
    }
  };

  const approveRequest = async () => {
    if (!activeOrderId || !order || !canApprove) return;
    setApproving(true);
    try {
      const approved = await api.post<{ issueOrder?: { order_number?: string } }>(`/wms/stock-requests/${activeOrderId}/approve`, {});
      toast.success(approved?.issueOrder?.order_number ? `Approved · WMI ${approved.issueOrder.order_number} created` : 'Request approved');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to approve request');
    } finally {
      setApproving(false);
    }
  };

  const submitRequest = async () => {
    if ((!activeOrderId && !isEditableDraft) || (order && !canSubmit)) return;
    if (!headerDraft) return;
    if (!lineDrafts.some(hasLineContent)) {
      toast.error('Add at least one line before submit');
      return;
    }
    setApproving(true);
    try {
      const savedOrderId = await saveHeader({ silent: true });
      if (!savedOrderId) return;
      const savedLines = await saveAllLines({ silent: true, orderIdOverride: savedOrderId });
      if (!savedLines) return;
      await api.post(`/wms/stock-requests/${savedOrderId}/submit`, {});
      toast.success('Request submitted for approval');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to submit request');
    } finally {
      setApproving(false);
    }
  };

  const reviseRequest = async () => {
    if (!activeOrderId || !order || !canRevise) return;
    const remark = window.prompt('Revise remark is required');
    if (!remark?.trim()) {
      toast.error('Revise remark is required');
      return;
    }
    setApproving(true);
    try {
      await api.post(`/wms/stock-requests/${activeOrderId}/revise`, { remark: remark.trim() });
      toast.success('Request revised to draft');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to revise request');
    } finally {
      setApproving(false);
    }
  };

  const rejectRequest = async () => {
    if (!activeOrderId || !order || !canReject) return;
    const remark = window.prompt('Reject remark is required');
    if (!remark?.trim()) {
      toast.error('Reject remark is required');
      return;
    }
    setApproving(true);
    try {
      await api.post(`/wms/stock-requests/${activeOrderId}/reject`, { remark: remark.trim() });
      toast.success('Request rejected');
      await fetchOrder();
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to reject request');
    } finally {
      setApproving(false);
    }
  };

  if (loadingMaster || loading || !headerDraft) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!order && activeOrderId) {
    return (
      <div className="space-y-4 p-4">
        <Button variant="outline" size="sm" onClick={closeOrBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          {embedded ? 'Close' : 'Back to Material Requests'}
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Material request not found
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn(
      'flex min-h-0 flex-col gap-4 p-4',
      embedded ? 'min-h-full overflow-visible pb-6' : 'h-full overflow-hidden',
    )}>
      <div className="space-y-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={closeOrBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {embedded ? 'Close' : 'Back to Material Requests'}
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{requestTitle}</h1>
            {order ? <Badge variant="outline" className={statusTone(order.status)}>{order.status}</Badge> : null}
            {(order?.project_code || headerDraft.projectCode) ? <Badge variant="outline">{order?.project_code ?? headerDraft.projectCode}</Badge> : null}
            <Badge variant="outline">{requestTitle}</Badge>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-start gap-2">
          {order ? (
            <Button
              variant="outline"
              onClick={() => {
              const requestForm = headerDraft.requestForm;
              const sharedRow = {
                requestNo: order.order_number,
                requestForm,
                status: order.status,
                requestDate: headerDraft.requestDate,
                projectCode: headerDraft.projectCode,
                jobNo: headerDraft.jobNo,
                contractor: headerDraft.contractor,
                requestedBy: headerDraft.requestedBy,
                authorisedOrApprovedBy: requestForm === 'PIPE' ? headerDraft.authorisedBy : headerDraft.approvedBy,
                issuedBy: headerDraft.issuedBy,
                updatedBy: requestForm === 'PIPE' ? '' : headerDraft.updatedBy,
                note: headerDraft.notes,
              };
              const columns = [
                { key: 'requestNo', label: 'Request No' },
                { key: 'requestForm', label: 'Request Form' },
                { key: 'status', label: 'Status' },
                { key: 'requestDate', label: 'Request Date' },
                { key: 'projectCode', label: 'Project' },
                { key: 'jobNo', label: 'Project / Job No' },
                { key: 'contractor', label: 'Contractor' },
                { key: 'requestedBy', label: requestByLabel(requestForm) },
                { key: 'authorisedOrApprovedBy', label: requestForm === 'PIPE' ? 'Authorised By' : 'Approved By' },
                { key: 'issuedBy', label: issuedByLabel(requestForm) },
                { key: 'updatedBy', label: 'Updated By' },
                { key: 'lineNo', label: 'Line' },
                { key: 'materialCode', label: 'Material Code' },
                { key: 'materialSpec', label: 'Material / Spec' },
                ...(requestForm === 'GENERAL' ? [{ key: 'materialType', label: 'Type' }] : []),
                ...(requestForm === 'PIPE' ? [
                  { key: 'nominalSize', label: 'Nominal Size' },
                  { key: 'schedule', label: 'Sch' },
                  { key: 'length', label: 'Length' },
                ] : []),
                ...(requestForm === 'PLATE' ? [
                  { key: 'stockNo', label: 'Stock No' },
                  { key: 'heatNo', label: 'Heat No' },
                  { key: 'dimension', label: 'Dimension' },
                  { key: 'grade', label: 'Grade' },
                  { key: 'millCert', label: 'Mill Cert' },
                ] : []),
                { key: 'qtyReq', label: 'Request Qty' },
                { key: 'uom', label: 'UOM' },
                ...(requestForm === 'PIPE' || requestForm === 'PLATE' ? [{ key: 'location', label: 'Location' }] : []),
                ...(requestForm === 'PLATE' ? [
                  { key: 'checkedRef', label: 'Checked Plate / SRF / Heat No' },
                  { key: 'receivedByContractorDate', label: 'Received By Contractor & Date' },
                  { key: 'returnQty', label: 'Return Qty' },
                  { key: 'irtNo', label: 'IRT No' },
                  { key: 'lineDate', label: 'Date' },
                ] : []),
                ...(requestForm === 'PIPE' ? [{ key: 'qtyIssued', label: 'Qty Issue' }] : []),
                { key: 'remark', label: 'Remark' },
              ];
              const rows = lineDrafts.map((line, index) => ({
                ...sharedRow,
                lineNo: index + 1,
                materialCode: line.skuCode,
                materialSpec: line.skuName,
                materialType: requestForm === 'PIPE' ? 'PIPE' : requestForm === 'PLATE' ? 'PLATE' : line.materialType,
                nominalSize: requestForm === 'PIPE' ? line.nominalSize : '',
                schedule: requestForm === 'PIPE' ? line.schedule : '',
                length: requestForm === 'PIPE' ? line.length : '',
                qtyReq: line.qtyReq,
                uom: line.uom,
                stockNo: requestForm === 'PLATE' ? line.stockNo : '',
                ...(requestForm === 'PLATE' ? { heatNo: line.heatNo } : {}),
                dimension: requestForm === 'PLATE' ? line.dimension : '',
                grade: requestForm === 'PLATE' ? line.grade : '',
                millCert: requestForm === 'PLATE' ? boolLabel(line.millCert) : '',
                location: requestForm === 'PIPE' || requestForm === 'PLATE' ? line.location : '',
                checkedRef: requestForm === 'PLATE' ? line.checkedRef : '',
                receivedByContractorDate: requestForm === 'PLATE' ? line.receivedByContractorDate : '',
                returnQty: requestForm === 'PLATE' ? line.returnQty : '',
                irtNo: requestForm === 'PLATE' ? line.irtNo : '',
                lineDate: requestForm === 'PLATE' ? line.lineDate : '',
                qtyIssued: requestForm === 'PIPE' ? line.qtyIssued : '',
                remark: line.remark,
              }));
              downloadCsv(`${order.order_number}-request-slip.csv`, columns, rows);
            }}
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          ) : null}
          {order ? (
            <Button
              variant="outline"
              onClick={() => {
              const requestForm = headerDraft.requestForm;
              const metaCards = [
                { label: 'Request No', value: order.order_number },
                { label: 'Request Form', value: requestTitle },
                { label: 'Status', value: order.status },
                { label: 'Request Date', value: headerDraft.requestDate || '-' },
                { label: 'Project', value: headerDraft.projectCode || '-' },
                { label: 'Project / Job No', value: headerDraft.jobNo || '-' },
                { label: 'Contractor', value: headerDraft.contractor || '-' },
                { label: requestByLabel(requestForm), value: headerDraft.requestedBy || '-' },
                { label: requestForm === 'PIPE' ? 'Authorised By' : 'Approved By', value: requestForm === 'PIPE' ? (headerDraft.authorisedBy || '-') : (headerDraft.approvedBy || '-') },
                { label: issuedByLabel(requestForm), value: headerDraft.issuedBy || '-' },
                { label: 'Updated By', value: requestForm === 'PIPE' ? '-' : (headerDraft.updatedBy || '-') },
                { label: 'Request Note', value: headerDraft.notes || '-' },
              ];
              const columnLabels = [
                'Line',
                'Material Code',
                'Material / Spec',
                ...(requestForm === 'PIPE' ? ['Nominal Size', 'Sch', 'Length', 'Location'] : []),
                ...(requestForm === 'PLATE' ? ['Stock No'] : requestForm === 'GENERAL' ? ['Type'] : []),
                'Request Qty',
                'UOM',
                ...(requestForm === 'PLATE' ? ['Heat No'] : []),
                ...(requestForm === 'PLATE' ? ['Dimension', 'Grade', 'Mill Cert', 'Location', 'Checked Plate / SRF / Heat No', 'Received By Contractor & Date', 'Return Qty', 'IRT No', 'Date'] : []),
                ...(requestForm === 'PIPE' ? ['Qty Issue'] : []),
                'Remark',
              ];
              const rowsHtml = lineDrafts.map((line, index) => {
                const cells = [
                  index + 1,
                  line.skuCode || '-',
                  line.skuName || '-',
                ...(requestForm === 'PIPE' ? [line.nominalSize || '-', line.schedule || '-', line.length || '-', line.location || '-'] : []),
                  ...(requestForm === 'PLATE' ? [line.stockNo || '-'] : requestForm === 'GENERAL' ? [line.materialType || '-'] : []),
                  line.qtyReq || '0',
                  line.uom || '-',
                  ...(requestForm === 'PLATE' ? [line.heatNo || '-'] : []),
                  ...(requestForm === 'PLATE' ? [line.dimension || '-', line.grade || '-', boolLabel(line.millCert), line.location || '-', line.checkedRef || '-', line.receivedByContractorDate || '-', line.returnQty || '-', line.irtNo || '-', line.lineDate || '-'] : []),
                  ...(requestForm === 'PIPE' ? [line.qtyIssued || '0'] : []),
                  line.remark || '-',
                ];
                return `<tr>${cells.map((cell) => `<td>${escapeHtml(String(cell))}</td>`).join('')}</tr>`;
              }).join('');
              openPrintableHtml({
                title: `${order.order_number} ${requestTitle}`,
                landscape: requestForm !== 'GENERAL',
                variant: 'legacy',
                bodyHtml: `
                  <div class="legacy-doc">
                    <div class="legacy-title">
                      <h1>${escapeHtml(requestTitle)}</h1>
                      <p>Wasco Request Slip Layout</p>
                    </div>
                    <table class="legacy-meta-table">
                      <tbody>
                        <tr>
                          <th>Request No</th><td>${escapeHtml(order.order_number)}</td>
                          <th>Request Form</th><td>${escapeHtml(requestTitle)}</td>
                          <th>Status</th><td>${escapeHtml(order.status)}</td>
                          <th>Request Date</th><td>${escapeHtml(headerDraft.requestDate || '-')}</td>
                        </tr>
                        <tr>
                          <th>Project</th><td>${escapeHtml(headerDraft.projectCode || '-')}</td>
                          <th>Project / Job No</th><td>${escapeHtml(headerDraft.jobNo || '-')}</td>
                          <th>Contractor</th><td>${escapeHtml(headerDraft.contractor || '-')}</td>
                          <th>${escapeHtml(requestByLabel(requestForm))}</th><td>${escapeHtml(headerDraft.requestedBy || '-')}</td>
                        </tr>
                        <tr>
                          <th>${escapeHtml(requestForm === 'PIPE' ? 'Authorised By' : 'Approved By')}</th><td>${escapeHtml(requestForm === 'PIPE' ? (headerDraft.authorisedBy || '-') : (headerDraft.approvedBy || '-'))}</td>
                          <th>${escapeHtml(issuedByLabel(requestForm))}</th><td>${escapeHtml(headerDraft.issuedBy || '-')}</td>
                          <th>Updated By</th><td>${escapeHtml(requestForm === 'PIPE' ? '-' : (headerDraft.updatedBy || '-'))}</td>
                          <th>Request Note</th><td>${escapeHtml(headerDraft.notes || '-')}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div>
                      <h2 class="section-title">Line Items</h2>
                      <table class="legacy-line-table">
                        <thead><tr>${columnLabels.map((label) => `<th>${escapeHtml(label)}</th>`).join('')}</tr></thead>
                        <tbody>${rowsHtml}</tbody>
                      </table>
                    </div>
                    <div class="legacy-signoff">
                      <div>${escapeHtml(requestByLabel(requestForm))}</div>
                      <div>${escapeHtml(requestForm === 'PIPE' ? 'Authorised By' : 'Approved By')}</div>
                      <div>${escapeHtml(issuedByLabel(requestForm))}</div>
                    </div>
                  </div>`,
              });
            }}
            >
              <Printer className="mr-2 h-4 w-4" />
              Print Slip
            </Button>
          ) : null}
          {order && canSubmit ? (
            <Button onClick={() => void submitRequest()} disabled={approving}>
              {approving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Submit
            </Button>
          ) : null}
          {order && canApprove ? (
            <Button onClick={() => void approveRequest()} disabled={approving}>
              {approving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Approve
            </Button>
          ) : null}
          {order && canReject ? (
            <Button variant="outline" onClick={() => void rejectRequest()} disabled={approving}>
              Reject
            </Button>
          ) : null}
          {order && canRevise ? (
            <Button variant="outline" onClick={() => void reviseRequest()} disabled={approving}>
              {approving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Revise
            </Button>
          ) : null}
          {!order || isEditableDraft ? (
            <Button onClick={() => void saveHeader()} disabled={savingHeader || !isEditableDraft}>
              {savingHeader ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              {order ? 'Save Header' : 'Create Header & Continue'}
            </Button>
          ) : null}
        </div>
      </div>
      {order && canApprove ? (
        <div className="mt-2 text-sm text-amber-800">
          Approval based on data: <span className="font-medium">{approvalOwnerLabel}</span>
        </div>
      ) : null}

      {order ? (
        <div className={cn(
          'rounded-md border px-3 py-2 text-sm',
          isEditableDraft
            ? 'border-blue-200 bg-blue-50 text-blue-800'
            : 'border-amber-200 bg-amber-50 text-amber-800',
        )}>
          {isEditableDraft
            ? 'Draft status: header and lines are editable. Save or submit when ready.'
            : canApprove
              ? 'Submitted for approval: document is read-only. Use Approve, Reject, or Revise at the top.'
              : `Status ${order.status}: document is read-only.`}
        </div>
      ) : null}

      <Card className="shrink-0">
        <CardHeader>
          <CardTitle>{requestTitle} Header</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className={cn(
            'grid gap-3 [&>div]:min-w-0',
            'grid-cols-1 md:grid-cols-2 xl:grid-cols-6',
          )}>
            <div>
              <Label>Request Date</Label>
              <Input className="mt-1" type="date" value={headerDraft.requestDate} disabled={!isEditableDraft} onChange={(e) => setHeaderDraft((prev) => prev ? { ...prev, requestDate: e.target.value } : prev)} />
            </div>
            <div>
              <Label>Request Form</Label>
              <Select value={headerDraft.requestForm} onValueChange={(value) => setHeaderDraft((prev) => prev ? { ...prev, requestForm: value } : prev)} disabled={!isEditableDraft}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HEADER_REQUEST_FORM_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project <span className="text-destructive">*</span></Label>
              <Select
                value={headerDraft.projectId}
                disabled={!isEditableDraft}
                onValueChange={(value) => {
                  const selectedProject = projects.find((project) => project.id === value) ?? null;
                  setHeaderDraft((prev) => prev ? {
                    ...prev,
                    projectId: value,
                    projectCode: selectedProject?.project_code ?? prev.projectCode,
                    jobNo: selectedProject?.project_code ?? prev.jobNo,
                  } : prev);
                }}
              >
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.project_code}{project.project_name ? ` · ${project.project_name}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={headerDraft.priority} onValueChange={(value) => setHeaderDraft((prev) => prev ? { ...prev, priority: value } : prev)} disabled={!isEditableDraft}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="xl:col-span-2">
              <Label>Contractor</Label>
              <Input className="mt-1" value={headerDraft.contractor} disabled={!isEditableDraft} onChange={(e) => setHeaderDraft((prev) => prev ? { ...prev, contractor: e.target.value } : prev)} />
            </div>
            <div>
              <Label>Approval <span className="text-destructive">*</span></Label>
              <Select value={headerDraft.approvalRequired} onValueChange={(value) => setHeaderDraft((prev) => prev ? { ...prev, approvalRequired: value } : prev)} disabled={!isEditableDraft}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Required</SelectItem>
                  <SelectItem value="false">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{requestByLabel(headerDraft.requestForm)} <span className="text-destructive">*</span></Label>
              <Select value={headerDraft.requestedBy} onValueChange={(value) => setHeaderDraft((prev) => prev ? { ...prev, requestedBy: value } : prev)} disabled={!isEditableDraft}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select requester" /></SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.display_name}>{sanitizeUserDisplay(user.display_name)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {headerDraft.requestForm === 'PIPE' ? (
              <div>
                <Label>Authorised By <span className="text-destructive">*</span></Label>
                <Select value={headerDraft.authorisedBy} onValueChange={(value) => setHeaderDraft((prev) => prev ? { ...prev, authorisedBy: value } : prev)} disabled={!isEditableDraft}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select approver" /></SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.display_name}>{sanitizeUserDisplay(user.display_name)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            {headerDraft.requestForm !== 'PIPE' ? (
              <div>
                <Label>Approved By <span className="text-destructive">*</span></Label>
                <Select value={headerDraft.approvedBy} onValueChange={(value) => setHeaderDraft((prev) => prev ? { ...prev, approvedBy: value } : prev)} disabled={!isEditableDraft}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select approver" /></SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.display_name}>{sanitizeUserDisplay(user.display_name)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <Label>{issuedByLabel(headerDraft.requestForm)}</Label>
              <Input className="mt-1" value={headerDraft.issuedBy} disabled />
            </div>
            <div className="xl:col-span-2">
              <Label>Request Note</Label>
              <Input className="mt-1" value={headerDraft.notes} disabled={!isEditableDraft} onChange={(e) => setHeaderDraft((prev) => prev ? { ...prev, notes: e.target.value } : prev)} />
            </div>
          </div>
        </CardContent>
      </Card>
      <Tabs
        defaultValue="items"
        className={cn(
          'flex min-h-0 flex-col',
          embedded ? 'overflow-visible' : 'flex-1 overflow-hidden',
        )}
      >
        <TabsList className="shrink-0 justify-start">
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="attachments">Attachment</TabsTrigger>
          <TabsTrigger value="eventLog">Event Log</TabsTrigger>
        </TabsList>
        <TabsContent
          value="items"
          className={cn(
            embedded ? 'overflow-visible' : 'min-h-0 flex-1 overflow-hidden',
          )}
        >
      <Card className={cn(
        'flex min-h-0 flex-col',
        embedded ? 'overflow-visible' : 'h-full overflow-hidden',
      )}>
        <CardHeader className="shrink-0 pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle>{requestTitle} Lines</CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setLineDrafts((prev) => [...prev, newLineDraft()])} disabled={!isEditableDraft}>
                <Plus className="mr-2 h-4 w-4" />
                Add Row
              </Button>
              <Button size="sm" onClick={() => void saveAllLines()} disabled={savingLines || !isEditableDraft}>
                {savingLines ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Save Lines
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className={cn(
          embedded ? 'overflow-visible' : 'min-h-0 flex-1 overflow-hidden',
        )}>
          {(() => {
            const requestForm = headerDraft.requestForm as RequestForm;
            const isPipe = requestForm === 'PIPE';
            const isPlate = requestForm === 'PLATE';

            return (
              <div className={cn(
                'runtime-grid-scrollbar boox-x-scroll min-h-0 overflow-auto rounded-lg border',
                !embedded && 'h-full',
              )}>
                {lineDrafts.length === 0 ? (
                  <div className="flex min-h-[240px] items-center justify-center border-b bg-muted/10 px-6 text-sm text-muted-foreground">
                    No lines yet. Click Add Row to add material line by line.
                  </div>
                ) : null}
                <table className="w-full min-w-[1020px] text-sm">
                  <thead className="bg-muted/60 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="w-14 px-3 py-2 text-left">Line</th>
                      <th className="min-w-[72px] px-3 py-2 text-left">Material Code</th>
                      <th className="min-w-[240px] px-3 py-2 text-left">Description</th>
                      <th className="min-w-[72px] px-3 py-2 text-left">Request Qty</th>
                      <th className="min-w-[110px] px-3 py-2 text-left">Request Length</th>
                      <th className="min-w-[120px] px-3 py-2 text-left">Remark</th>
                      <th className="min-w-[110px] px-3 py-2 text-left">UOM</th>
                      {isPlate ? <th className="min-w-[130px] px-3 py-2 text-left">Stock No</th> : null}
                      {!isPipe && !isPlate ? <th className="min-w-[110px] px-3 py-2 text-left">Type</th> : null}
                      {isPlate ? <th className="min-w-[130px] px-3 py-2 text-left">Heat No</th> : null}
                      {isPlate ? <th className="min-w-[150px] px-3 py-2 text-left">Dimension</th> : null}
                      {isPlate ? <th className="min-w-[120px] px-3 py-2 text-left">Grade</th> : null}
                      {isPlate ? <th className="w-28 px-3 py-2 text-left">Mill Cert</th> : null}
                      {isPipe || isPlate ? <th className="min-w-[130px] px-3 py-2 text-left">Location</th> : null}
                      {isPlate ? <th className="min-w-[190px] px-3 py-2 text-left">Checked Plate / SRF / Heat No</th> : null}
                      {isPlate ? <th className="min-w-[190px] px-3 py-2 text-left">Received By Contractor & Date</th> : null}
                      {isPlate ? <th className="min-w-[110px] px-3 py-2 text-left">Return Qty</th> : null}
                      {isPlate ? <th className="min-w-[120px] px-3 py-2 text-left">IRT No</th> : null}
                      {isPlate ? <th className="min-w-[140px] px-3 py-2 text-left">Date</th> : null}
                      {isPipe ? <th className="min-w-[72px] px-3 py-2 text-left">Qty Issue</th> : null}
                      <th className="w-12 px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineDrafts.map((line, index) => (
                      <tr key={line.id} className="border-t align-top">
                        <td className="px-3 py-2 font-medium">{index + 1}</td>
                        <td className="px-3 py-2">
                          <MaterialRequestSkuPicker
                            warehouseId={warehouseId || undefined}
                            projectId={headerDraft.projectId || undefined}
                            requestForm={requestForm}
                            value={line.skuCode || ''}
                            disabled={!isEditableDraft}
                            onSelect={(sku) => patchLine(line.id, buildSkuPatch(sku, requestForm, line))}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">{line.skuName || '-'}</div>
                        </td>
                        <td className="px-3 py-2">
                          <Input className="h-8" type="number" min="0" value={line.qtyReq} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { qtyReq: e.target.value })} placeholder="Qty requested" />
                        </td>
                        <td className="px-3 py-2">
                          <Input className="h-8" type="number" min="0" value={line.length} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { length: e.target.value })} placeholder="Length requested" />
                        </td>
                        <td className="px-3 py-2">
                          <Input className="h-8" value={line.remark} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { remark: e.target.value })} placeholder="Line remark" />
                        </td>
                        <td className="px-3 py-2">
                          <Select value={line.uom} onValueChange={(value) => patchLine(line.id, { uom: value })} disabled={!isEditableDraft}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {LINE_UOM_OPTIONS.map((option) => (
                                <SelectItem key={option} value={option}>{option}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        {isPlate ? <td className="px-3 py-2"><Input className="h-8" value={line.stockNo} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { stockNo: e.target.value })} /></td> : null}
                        {!isPipe && !isPlate ? (
                          <td className="px-3 py-2">
                            <Select value={line.materialType} onValueChange={(value) => patchLine(line.id, { materialType: value })} disabled={!isEditableDraft}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {LINE_MATERIAL_TYPE_OPTIONS.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                        ) : null}
                        {isPlate ? (
                          <td className="px-3 py-2">
                            <Input className="h-8" value={line.heatNo} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { heatNo: e.target.value })} placeholder="Heat No" />
                          </td>
                        ) : null}
                        {isPlate ? <td className="px-3 py-2"><Input className="h-8" value={line.dimension} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { dimension: e.target.value })} /></td> : null}
                        {isPlate ? <td className="px-3 py-2"><Input className="h-8" value={line.grade} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { grade: e.target.value })} /></td> : null}
                        {isPlate ? (
                          <td className="px-3 py-2">
                            <Checkbox checked={line.millCert} disabled={!isEditableDraft} onCheckedChange={(checked) => patchLine(line.id, { millCert: checked === true })} />
                          </td>
                        ) : null}
                        {isPipe || isPlate ? (
                          <td className="px-3 py-2">
                            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">{line.location || '-'}</div>
                          </td>
                        ) : null}
                        {isPlate ? (
                          <td className="px-3 py-2">
                            <Select value={line.checkedRef} onValueChange={(value) => patchLine(line.id, { checkedRef: value })} disabled={!isEditableDraft}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="YES">Checked</SelectItem>
                                <SelectItem value="NO">Not checked</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        ) : null}
                        {isPlate ? <td className="px-3 py-2"><Input className="h-8" value={line.receivedByContractorDate} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { receivedByContractorDate: e.target.value })} /></td> : null}
                        {isPlate ? <td className="px-3 py-2"><Input className="h-8" type="number" value={line.returnQty} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { returnQty: e.target.value })} /></td> : null}
                        {isPlate ? <td className="px-3 py-2"><Input className="h-8" value={line.irtNo} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { irtNo: e.target.value })} /></td> : null}
                        {isPlate ? <td className="px-3 py-2"><Input className="h-8" type="date" value={line.lineDate} disabled={!isEditableDraft} onChange={(e) => patchLine(line.id, { lineDate: e.target.value })} /></td> : null}
                        {isPipe ? (
                          <td className="px-3 py-2">
                            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                              {line.qtyIssued || '0'}
                            </div>
                          </td>
                        ) : null}
                        <td className="px-3 py-2">
                          <Button size="icon" variant="outline" onClick={() => void deleteLine(line)} disabled={savingLineId === line.id || savingLines || !isEditableDraft}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardContent>
      </Card>
        </TabsContent>
        <TabsContent value="attachments" className="min-h-0 flex-1 overflow-hidden">
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Attachment</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
              No attachment uploaded for this material request.
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="eventLog" className="min-h-0 flex-1 overflow-hidden">
          <Card className="h-full overflow-hidden">
            <CardHeader>
              <CardTitle>Event Log</CardTitle>
            </CardHeader>
            <CardContent className="h-full overflow-auto">
              {(() => {
                const logs = Array.isArray(order?.metadata?.audit_logs) ? order?.metadata?.audit_logs as Array<Record<string, unknown>> : [];
                if (logs.length === 0) {
                  return <div className="py-8 text-center text-sm text-muted-foreground">No event log yet.</div>;
                }
                return (
                  <div className="space-y-2">
                    {logs.slice().reverse().map((log, index) => (
                      <div key={String(log.id ?? index)} className="rounded-md border px-3 py-2 text-sm">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <span className="font-medium">{String(log.action ?? 'EVENT')}</span>
                          <span className="text-xs text-muted-foreground">{String(log.createdAt ?? '')}</span>
                        </div>
                        <div className="mt-1 text-muted-foreground">{String(log.message ?? '')}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {String(log.fromStatus ?? '-')} → {String(log.toStatus ?? '-')}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default function WmsMaterialRequestDetailPage() {
  const { id } = usePageParams<{ id?: string }>();
  const isMobile = useRenderMode() === 'mobile';
  if (isMobile || !id) return <WmsMaterialRequestMobileFlow />;
  return <DesktopMaterialRequestDetailPage />;
}

export function WmsMaterialRequestDetailPanel(props: WmsMaterialRequestDetailPanelProps) {
  return <DesktopMaterialRequestDetailPage {...props} embedded={props.embedded ?? true} />;
}
